import { useQuery } from "@apollo/client";
import { useCallback, useMemo } from "react";
import type { CustomerFilterInput, CustomersQuery } from "@/gql/graphql";
import { CUSTOMERS_QUERY } from "@/graphql/customers";
import { transformApolloError } from "@/lib/error/error-utils";

export interface UseCustomerQueryOptions {
  salonId?: string | null;
  filters?: CustomerFilterInput;
  skip?: boolean;
}
export const useCustomerQuery = ({ salonId, filters, skip = false }: UseCustomerQueryOptions) => {
  const {
    data,
    loading,
    error: apolloError,
    refetch,
    fetchMore,
    networkStatus,
  } = useQuery<CustomersQuery>(CUSTOMERS_QUERY, {
    variables: {
      salonId: salonId || "",
      filters: {
        ...filters,
        page: 1,
        limit: 20,
      },
    },
    skip: salonId === null || skip,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: "cache-and-network",
    errorPolicy: "all",
  });

  const error = useMemo(() => {
    if (!apolloError) return null;
    return transformApolloError(apolloError);
  }, [apolloError]);

  const loadMore = useCallback(async () => {
    if (!data?.customers?.pagination?.hasNext || loading) return;

    const currentPage = data.customers.pagination.page || 1;
    const nextPage = currentPage + 1;

    try {
      await fetchMore({
        variables: {
          salonId: salonId || "",
          filters: {
            ...filters,
            page: nextPage,
            limit: 20,
          },
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult?.customers?.customers) return prev;

          return {
            ...prev,
            customers: {
              ...fetchMoreResult.customers,
              customers: [
                ...(prev.customers?.customers || []),
                ...fetchMoreResult.customers.customers,
              ],
            },
          };
        },
      });
    } catch (error) {
      console.error("Error loading more customers:", error);
    }
  }, [
    data?.customers?.pagination?.hasNext,
    data?.customers?.pagination?.page,
    loading,
    fetchMore,
    salonId,
    filters,
  ]);

  return {
    data: data?.customers?.customers || [],
    pagination: data?.customers?.pagination,
    loading,
    error,
    refetch,
    loadMore,
    hasMore: data?.customers?.pagination?.hasNext || false,
    networkStatus,
  };
};
